<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  importItem: { type: Object, default: null },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();

const store = useStore();
const inboxesList = useMapGetter('inboxes/getInboxes');

onMounted(() => {
  store.dispatch('inboxes/get');
});

const form = ref({
  name: '',
  spreadsheet_id: '',
  sheet_name: '',
  api_url: '',
  api_method: 'POST',
  api_headers: '{}',
  payload_template: '{}',
  dedup_key_column: '',
  last_processed_row: 0,
  sync_interval: null,
  send_to_second_number: false,
  second_number: '',
  import_type: 'webhook',
  inbox_id: null,
});

const isEditing = ref(false);

watch(
  () => props.show,
  val => {
    if (val && props.importItem) {
      isEditing.value = true;
      form.value = {
        name: props.importItem.name || '',
        spreadsheet_id: props.importItem.spreadsheet_id || '',
        sheet_name: props.importItem.sheet_name || '',
        api_url: props.importItem.api_url || '',
        api_method: props.importItem.api_method || 'POST',
        api_headers: JSON.stringify(props.importItem.api_headers || {}, null, 2),
        payload_template: JSON.stringify(
          props.importItem.payload_template || {},
          null,
          2
        ),
        dedup_key_column: props.importItem.dedup_key_column || '',
        last_processed_row: props.importItem.last_processed_row || 0,
        sync_interval: props.importItem.sync_interval || null,
        send_to_second_number: props.importItem.send_to_second_number || false,
        second_number: props.importItem.second_number || '',
        import_type: props.importItem.import_type || 'webhook',
        inbox_id: props.importItem.inbox_id || null,
      };
    } else if (val) {
      isEditing.value = false;
      form.value = {
        name: '',
        spreadsheet_id: '',
        sheet_name: '',
        api_url: '',
        api_method: 'POST',
        api_headers: '{}',
        payload_template: '{}',
        dedup_key_column: '',
        last_processed_row: 0,
        sync_interval: null,
        send_to_second_number: false,
        second_number: '',
        import_type: 'webhook',
        inbox_id: null,
      };
    }
  }
);

const handleSubmit = () => {
  let parsedHeaders = {};
  let parsedTemplate = {};

  try {
    parsedHeaders = JSON.parse(form.value.api_headers);
  } catch {
    parsedHeaders = {};
  }
  try {
    parsedTemplate = JSON.parse(form.value.payload_template);
  } catch {
    parsedTemplate = {};
  }

  const payload = {
    google_sheet_import: {
      name: form.value.name,
      spreadsheet_id: form.value.spreadsheet_id,
      sheet_name: form.value.sheet_name,
      api_url: form.value.api_url,
      api_method: form.value.api_method,
      api_headers: parsedHeaders,
      payload_template: parsedTemplate,
      dedup_key_column: form.value.dedup_key_column,
      last_processed_row: form.value.last_processed_row,
      sync_interval: form.value.sync_interval ? parseInt(form.value.sync_interval, 10) : null,
      send_to_second_number: form.value.send_to_second_number,
      second_number: form.value.second_number,
      import_type: form.value.import_type,
      inbox_id: form.value.inbox_id ? parseInt(form.value.inbox_id, 10) : null,
    },
  };

  if (isEditing.value && props.importItem) {
    payload.id = props.importItem.id;
  }

  emit('save', payload);
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-5"
      @click.self="emit('close')"
    >
      <div
        class="bg-n-solid-1 rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-n-weak"
      >
        <div class="flex items-center justify-between p-6 border-b border-n-weak">
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{
              isEditing
                ? t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.EDIT_TITLE')
                : t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.CREATE_TITLE')
            }}
          </h3>
          <button
            class="text-n-slate-11 hover:text-n-slate-12 text-lg"
            @click="emit('close')"
          >
            ✕
          </button>
        </div>

        <form class="p-6 flex flex-col gap-4" @submit.prevent="handleSubmit">
          <!-- Name -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.NAME') }}
            </label>
            <input
              v-model="form.name"
              type="text"
              required
              class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
              placeholder="Ej: Leads TikTok"
            />
          </div>

          <!-- Import Type -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.IMPORT_TYPE') }}
            </label>
            <select
              v-model="form.import_type"
              class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
            >
              <option value="webhook">
                {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.IMPORT_TYPES.WEBHOOK') }}
              </option>
              <option value="direct_contact">
                {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.IMPORT_TYPES.DIRECT_CONTACT') }}
              </option>
            </select>
          </div>

          <!-- Target Inbox -->
          <div v-if="form.import_type === 'direct_contact'">
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.TARGET_INBOX_LABEL') }}
            </label>
            <select
              v-model="form.inbox_id"
              required
              class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
            >
              <option :value="null" disabled>
                {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SELECT_INBOX_PLACEHOLDER') }}
              </option>
              <option
                v-for="inbox in inboxesList"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }} ({{ inbox.channel_type }})
              </option>
            </select>
            <p class="text-xs text-n-slate-10 mt-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.TARGET_INBOX_HELP') }}
            </p>
          </div>

          <!-- Guidelines for Direct Import -->
          <div
            v-if="form.import_type === 'direct_contact'"
            class="p-4 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-11 flex flex-col gap-2 mt-1"
          >
            <div class="flex items-center gap-2 font-semibold text-sm text-n-slate-12">
              <i class="i-lucide-info text-base flex-shrink-0 text-n-brand" />
              <span>{{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.DIRECT_CONTACT_HELP_TITLE') }}</span>
            </div>
            <p class="text-xs">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.DIRECT_CONTACT_HELP_DESC') }}
            </p>
            <ul class="list-disc pl-4 text-xs flex flex-col gap-1">
              <li>{{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.DIRECT_CONTACT_HELP_RULE_1') }}</li>
              <li>{{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.DIRECT_CONTACT_HELP_RULE_2') }}</li>
              <li>{{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.DIRECT_CONTACT_HELP_RULE_3') }}</li>
              <li>{{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.DIRECT_CONTACT_HELP_RULE_4') }}</li>
            </ul>
          </div>

          <!-- Spreadsheet ID -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SPREADSHEET_ID') }}
            </label>
            <input
              v-model="form.spreadsheet_id"
              type="text"
              required
              class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms"
            />
            <p class="text-xs text-n-slate-10 mt-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_CONTACTS.SPREADSHEET_HELP') }}
            </p>
          </div>

          <!-- Sheet Name -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SHEET_NAME') }}
            </label>
            <input
              v-model="form.sheet_name"
              type="text"
              class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
              placeholder="Hoja 1"
            />
          </div>

          <!-- Webhook Developer Settings -->
          <details
            v-if="form.import_type === 'webhook'"
            open
            class="group border border-n-weak rounded-md bg-n-alpha-1 p-4 mt-2"
          >
            <summary class="flex justify-between items-center font-medium text-sm text-n-slate-12 cursor-pointer list-none select-none">
              <span>{{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.WEBHOOK_SETTINGS_TITLE') }}</span>
              <span class="transition group-open:rotate-180">
                <i class="i-lucide-chevron-down text-sm" />
              </span>
            </summary>
            
            <div class="mt-4 flex flex-col gap-4">
              <!-- API URL -->
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">
                  {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.API_URL') }}
                </label>
                <input
                  v-model="form.api_url"
                  type="url"
                  class="w-full rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
                  placeholder="https://api.example.com/webhook"
                />
              </div>

              <!-- API Method -->
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">
                  {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.API_METHOD') }}
                </label>
                <select
                  v-model="form.api_method"
                  class="w-full rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-sm"
                >
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                </select>
              </div>

              <!-- API Headers (JSON) -->
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">
                  {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.API_HEADERS') }}
                </label>
                <textarea
                  v-model="form.api_headers"
                  rows="3"
                  class="w-full rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-sm font-mono focus:border-n-brand focus:ring-n-brand"
                  placeholder='{ "Authorization": "Bearer xxx" }'
                />
              </div>

              <!-- Payload Template (JSON) -->
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">
                  {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.PAYLOAD_TEMPLATE') }}
                </label>
                <textarea
                  v-model="form.payload_template"
                  rows="8"
                  class="w-full rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-sm font-mono focus:border-n-brand focus:ring-n-brand"
                  placeholder='{ "field": "value" }'
                />
                <p class="text-xs text-n-slate-10 mt-1">
                  {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.TEMPLATE_HELP') }}
                </p>
              </div>
            </div>
          </details>

          <!-- Send to Second Number -->
          <div class="flex items-center gap-2 mt-2">
            <input
              id="send_to_second_number"
              v-model="form.send_to_second_number"
              type="checkbox"
              class="h-4 w-4 rounded border-n-weak bg-n-alpha-1 text-n-brand focus:ring-n-brand"
            />
            <label for="send_to_second_number" class="text-sm font-medium text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SEND_TO_SECOND_NUMBER') }}
            </label>
          </div>

          <!-- Second Number Input -->
          <div v-if="form.send_to_second_number">
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SECOND_NUMBER') }}
            </label>
            <input
              v-model="form.second_number"
              type="text"
              class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
              placeholder="Ej: {{ $json.Telefono2 }} o 51999999999"
            />
            <p class="text-xs text-n-slate-10 mt-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SECOND_NUMBER_HELP') }}
            </p>
          </div>

          <!-- Advanced Settings -->
          <div class="border-t border-n-weak pt-4 mt-2">
             <h4 class="text-sm font-semibold text-n-slate-12 mb-3">
               {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.ADVANCED_SETTINGS_TITLE') }}
             </h4>
             
             <div class="grid grid-cols-2 gap-4">
                <!-- Dedup Key Column -->
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.DEDUP_COLUMN_LABEL') }}
                  </label>
                  <input
                    v-model="form.dedup_key_column"
                    type="text"
                    class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
                    placeholder="Ej: Email o Lead ID"
                  />
                  <p class="text-xs text-n-slate-10 mt-1">
                     {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.DEDUP_COLUMN_HELP') }}
                  </p>
                </div>

                <!-- Last Processed Row -->
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.LAST_PROCESSED_ROW_LABEL') }}
                  </label>
                  <input
                    v-model="form.last_processed_row"
                    type="number"
                    class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
                  />
                  <p class="text-xs text-n-slate-10 mt-1">
                     {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.LAST_PROCESSED_ROW_HELP') }}
                  </p>
                </div>
             </div>

             <!-- Sync Interval -->
             <div class="mt-4">
               <label class="block text-sm font-medium text-n-slate-12 mb-1">
                 {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SYNC_INTERVAL_LABEL') }}
               </label>
               <select
                 v-model="form.sync_interval"
                 class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm"
               >
                 <option :value="null">
                   {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SYNC_INTERVAL_OPTIONS.MANUAL') }}
                 </option>
                 <option :value="5">
                   {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SYNC_INTERVAL_OPTIONS.MIN_5') }}
                 </option>
                 <option :value="10">
                   {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SYNC_INTERVAL_OPTIONS.MIN_10') }}
                 </option>
                 <option :value="15">
                   {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SYNC_INTERVAL_OPTIONS.MIN_15') }}
                 </option>
                 <option :value="30">
                   {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SYNC_INTERVAL_OPTIONS.MIN_30') }}
                 </option>
                 <option :value="60">
                   {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SYNC_INTERVAL_OPTIONS.HOUR_1') }}
                 </option>
                 <option :value="360">
                   {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SYNC_INTERVAL_OPTIONS.HOUR_6') }}
                 </option>
                 <option :value="720">
                   {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SYNC_INTERVAL_OPTIONS.HOUR_12') }}
                 </option>
                 <option :value="1440">
                   {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SYNC_INTERVAL_OPTIONS.DAY_1') }}
                 </option>
               </select>
               <p class="text-xs text-n-slate-10 mt-1">
                 {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SYNC_INTERVAL_HELP') }}
               </p>
             </div>
          </div>

          <!-- Actions -->
          <div class="flex justify-end gap-2 pt-2 border-t border-n-weak">
            <Button
              faded
              slate
              :label="t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.CANCEL')"
              @click="emit('close')"
            />
            <Button
              blue
              :label="
                isEditing
                  ? t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.UPDATE')
                  : t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.CREATE')
              "
              type="submit"
            />
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>
